import React, { useEffect, useState } from 'react';
import {
  AuditLogDetailsWrapper,
  AuditLogEventName,
} from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Typography } from '@mui/material';
import { IAuditLogItemResponse } from 'models/auditLog';
import Grid from '@mui/material/Grid';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { useFirearms } from 'pages/Firearms/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { useTranslations } from 'hooks/useTranslations';
import { ITransactionDetails } from 'models/transaction';
import { isStringEmpty } from 'utils/string';
import { findInDictionary } from 'helpers/dictionary';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { EventTypes } from 'pages/AuditLogPage/helpers';
import { isTransactionDeleted } from 'requests/transaction';

interface ITransactionEventDetails {
  auditLog: IAuditLogItemResponse;
}

const TransactionEventDetails = ({ auditLog }: ITransactionEventDetails) => {
  const [transactionDetails, setTransactionDetails] = useState<ITransactionDetails | null>();
  const [previoustransactionDetails, setPreviousTransactionDetails] =
    useState<ITransactionDetails | null>();
  const [previousType, setPreviousType] = useState<string | null>();
  const [isTransactionDetails, setIsTransactionDetails] = useState(false);
  const { loadDictionaries, transactionType } = useFirearms();
  const { selectedLanguage } = useLang();
  const navigate = useNavigate();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const { t } = useTranslations();

  useEffect(() => {
    (async () => {
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    (async () => {
      if (auditLog && auditLog.objectUniqueId) {
        const isDeleted = await isTransactionDeleted(auditLog.objectUniqueId);
        setIsTransactionDetails(!isDeleted);
      }

      if (auditLog && auditLog?.postObjectValue) {
        const postObjectValue = auditLog.postObjectValue as any;
        setTransactionDetails(postObjectValue);
      }
      if (auditLog && auditLog?.previousObjectValue) {
        const previousObjectValue = auditLog.previousObjectValue as any;
        setPreviousTransactionDetails(previousObjectValue);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditLog]);

  useEffect(() => {
    if (transactionType?.length && previoustransactionDetails) {
      setPreviousType(
        findInDictionary(previoustransactionDetails?.typeUniqueId, transactionType),
      );
    }
  }, [transactionType, previoustransactionDetails]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.TransactionType]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  let transactionName = '';
  if (transactionType?.length) {
    const transactionTypeItem = transactionType.find(
      (type) => type.uniqueId === transactionDetails?.typeUniqueId,
    );
    if (transactionTypeItem) {
      transactionName = transactionTypeItem.name;
    }
  }

  return (
    <>
      <AuditLogEventName>
        <Typography variant="h5">{t('auditLogDetails.transactionsDetails.title')}</Typography>
      </AuditLogEventName>

      {previoustransactionDetails && (
        <AuditLogDetailsWrapper container rowSpacing={2} sx={{ marginTop: '-6px' }}>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {`${t('auditLogDetails.transactionsDetails.title')} ${t(
                'auditLogDetails.before.event.title',
              )}`}
            </Typography>
            <Typography variant="body2">
              <Chips text={emptyFieldHolder(previousType)} variant={EChipsVariants.primary} />
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.transactionDateAndTime')}
            </Typography>
            <Typography variant="body2">
              {previoustransactionDetails?.date
                ? formatDate(previoustransactionDetails.date, longDateFormat)
                : undefined}
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.createdOn')}
            </Typography>
            <Typography variant="body2">
              {formatDate(previoustransactionDetails.createdDate, longDateFormat)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.createdBy')}
            </Typography>
            <Typography variant="body2">{previoustransactionDetails?.createdBy}</Typography>
          </Grid>
          {isTransactionDetails && auditLog.eventTypeUniqueId !== EventTypes.DISCARD && (
            <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.small}
                onClick={() =>
                  navigate(
                    ERouteLinks.ViewTransaction.replace(
                      ':id',
                      previoustransactionDetails.uniqueId,
                    ),
                  )
                }
                label={t('auditLogDetails.transactionsDetails.details')}
                type="button"
                fullWidth
                sx={{
                  mt: {
                    lg: 1,
                    sm: 3.5,
                  },
                }}
              />
            </Grid>
          )}
        </AuditLogDetailsWrapper>
      )}
      {transactionDetails && (
        <AuditLogDetailsWrapper
          container
          columns={10}
          rowSpacing={2}
          sx={{ marginTop: '-6px' }}
        >
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.title')}
            </Typography>
            <Typography variant="body2">
              {transactionName ? (
                <Chips text={transactionName as string} variant={EChipsVariants.primary} />
              ) : (
                ''
              )}
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.transactionDateAndTime')}
            </Typography>
            <Typography variant="body2">
              {transactionDetails?.date
                ? formatDate(transactionDetails.date, longDateFormat)
                : undefined}
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.createdOn')}
            </Typography>
            <Typography variant="body2">
              {formatDate(transactionDetails.createdDate, longDateFormat)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
              {t('auditLogDetails.transactionsDetails.createdBy')}
            </Typography>
            <Typography variant="body2">
              {isStringEmpty(transactionDetails?.createdBy)
                ? transactionDetails?.createdBy
                : `${auditLog.author.firstName} ${auditLog.author.lastName}`}
            </Typography>
          </Grid>
          {isTransactionDetails && (
            <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.outlined}
                size={EButtonSizes.small}
                onClick={() =>
                  navigate(
                    ERouteLinks.ViewTransaction.replace(':id', transactionDetails.uniqueId),
                  )
                }
                label={t('auditLogDetails.transactionsDetails.details')}
                type="button"
                fullWidth
                sx={{
                  mt: {
                    lg: 1,
                    sm: 3.5,
                  },
                }}
              />
            </Grid>
          )}
        </AuditLogDetailsWrapper>
      )}
    </>
  );
};

export default TransactionEventDetails;
